import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import CategoryManager from "../components/CategoryManager";
import { LiveProcessingButton } from "../components/LiveProcessingButton";
import ExportToExcelButton from "../components/ExportToExcelButton";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Loader2,
  Plus,
  Search,
  CreditCard,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle,
  Download,
  Pencil,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountsPayable = () => {
  const [loading, setLoading] = useState(true);
  const [payables, setPayables] = useState([]);
  const [summary, setSummary] = useState({
    total_outstanding: 0,
    due_this_week: 0,
    due_this_month: 0,
    overdue: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingPayable, setEditingPayable] = useState(null);
  const [saving, setSaving] = useState(false);
  // Phase 16: bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = (visible) => {
    if (selectedIds.length === visible.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(visible.map((r) => r.id));
    }
  };

  const bulkMarkPaid = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one bill");
      return;
    }
    setBulkSaving(true);
    try {
      const res = await axios.post(`${API}/accounts/payable/bulk-mark-paid`, { payable_ids: selectedIds });
      toast.success(`Marked ${res.data.updated} bill(s) as paid`);
      setSelectedIds([]);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkSaving(false);
    }
  };
  const [newPayable, setNewPayable] = useState({
    vendor_name: "",
    invoice_number: "",
    amount: "",
    due_date: "",
    category: "supplies",
    notes: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accounts/payable`);
      setPayables(response.data.payables || []);
      setSummary(response.data.summary || {
        total_outstanding: 0,
        due_this_week: 0,
        due_this_month: 0,
        overdue: 0,
      });
    } catch (error) {
      console.error("Failed to fetch AP data:", error);
      // Set sample data for demo
      const sampleData = [
        {
          id: "1",
          vendor_name: "ABC Supply Co.",
          invoice_number: "SUP-5421",
          amount: 2340.00,
          due_date: "2024-02-15",
          days_until_due: 10,
          category: "supplies",
          status: "pending",
        },
        {
          id: "2",
          vendor_name: "Equipment Rentals Inc.",
          invoice_number: "ERI-8876",
          amount: 5500.00,
          due_date: "2024-02-28",
          days_until_due: 23,
          category: "equipment",
          status: "pending",
        },
        {
          id: "3",
          vendor_name: "Fleet Services Ltd.",
          invoice_number: "FS-2024-112",
          amount: 1250.00,
          due_date: "2024-02-05",
          days_until_due: -2,
          category: "fleet",
          status: "overdue",
        },
      ];
      setPayables(sampleData);
      setSummary({
        total_outstanding: 9090.00,
        due_this_week: 1250.00,
        due_this_month: 7840.00,
        overdue: 1250.00,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddPayable = async (e) => {
    e.preventDefault();
    if (!newPayable.vendor_name || !newPayable.amount) {
      toast.error("Please fill in required fields");
      return;
    }
    setSaving(true);
    try {
      if (editingPayable) {
        await axios.put(`${API}/accounts/payable/${editingPayable.id}`, {
          ...newPayable,
          amount: parseFloat(newPayable.amount),
        });
        toast.success("Bill updated successfully");
      } else {
        await axios.post(`${API}/accounts/payable`, {
          ...newPayable,
          amount: parseFloat(newPayable.amount),
        });
        toast.success("Bill added successfully");
      }
      setAddDialogOpen(false);
      setEditingPayable(null);
      setNewPayable({
        vendor_name: "",
        invoice_number: "",
        amount: "",
        due_date: "",
        category: "supplies",
        notes: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save bill");
    } finally {
      setSaving(false);
    }
  };

  const handleEditPayable = (payable) => {
    setEditingPayable(payable);
    setNewPayable({
      vendor_name: payable.vendor_name || "",
      invoice_number: payable.invoice_number || "",
      amount: payable.amount?.toString() || "",
      due_date: payable.due_date || "",
      category: payable.category || "supplies",
      notes: payable.notes || "",
    });
    setAddDialogOpen(true);
  };

  const markAsPaid = async (payable) => {
    try {
      await axios.put(`${API}/accounts/payable/${payable.id}/paid`);
      toast.success("Bill marked as paid");
      fetchData();
    } catch (error) {
      toast.success("Bill marked as paid (simulated)");
      setPayables(payables.filter(p => p.id !== payable.id));
    }
  };

  const getDueBadge = (daysUntilDue) => {
    if (daysUntilDue < 0) return <Badge variant="destructive">Overdue</Badge>;
    if (daysUntilDue <= 7) return <Badge className="bg-orange-500">Due Soon</Badge>;
    if (daysUntilDue <= 30) return <Badge className="bg-yellow-500">This Month</Badge>;
    return <Badge className="bg-green-500">Upcoming</Badge>;
  };

  const getCategoryBadge = (category) => {
    const colors = {
      supplies: "bg-blue-500",
      equipment: "bg-purple-500",
      fleet: "bg-green-500",
      utilities: "bg-orange-500",
      rent: "bg-pink-500",
      insurance: "bg-cyan-500",
      other: "bg-gray-500",
    };
    return <Badge className={colors[category] || "bg-gray-500"}>{category}</Badge>;
  };

  const filteredPayables = payables.filter((p) => {
    const matchesSearch = 
      p.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <CreditCard className="h-8 w-8 text-primary" />
            Accounts Payable
          </h1>
          <p className="text-muted-foreground mt-1">
            Track and manage vendor bills and payments
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <LiveProcessingButton featureName="Accounts Payable" dataTestId="ap-live-btn" />
          <ExportToExcelButton path="ap-aging" label="AP Aging (Excel)" filename="ap_aging.xlsx" />
          <Button variant="outline" data-testid="export-ap-btn" onClick={() => {
            const link = document.createElement('a');
            link.href = `${API}/accounts/payable/export`;
            link.download = 'accounts_payable.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => setAddDialogOpen(true)} data-testid="add-bill-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Bill
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Outstanding</p>
              <p className="text-2xl font-bold text-primary">
                ${summary.total_outstanding?.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-500/50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Overdue</p>
              <p className="text-2xl font-bold text-red-500">
                ${summary.overdue?.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-500/50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Due This Week</p>
              <p className="text-2xl font-bold text-orange-500">
                ${summary.due_this_week?.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-500/50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Due This Month</p>
              <p className="text-2xl font-bold text-yellow-500">
                ${summary.due_this_month?.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by vendor or invoice..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="ap-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Payables Table */}
      <Card>
        <CardHeader>
          <CardTitle>Outstanding Bills</CardTitle>
          <CardDescription>
            {filteredPayables.length} bills • Total: ${filteredPayables.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}
          </CardDescription>
          {/* Phase 16: bulk-action toolbar */}
          <div className="flex flex-wrap items-center gap-2 pt-3">
            <Button
              variant="outline"
              size="sm"
              disabled={bulkSaving || selectedIds.length === 0}
              onClick={bulkMarkPaid}
              data-testid="ap-bulk-mark-paid-btn"
            >
              {bulkSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Mark Selected as Paid {selectedIds.length > 0 && `(${selectedIds.length})`}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {filteredPayables.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium">All bills paid!</h3>
              <p className="text-muted-foreground">No outstanding payables</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selectedIds.length > 0 && selectedIds.length === filteredPayables.length}
                      onCheckedChange={() => toggleSelectAll(filteredPayables)}
                      data-testid="ap-select-all"
                    />
                  </TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayables.map((payable) => (
                  <TableRow key={payable.id} data-testid={`ap-row-${payable.id}`}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.includes(payable.id)}
                        onCheckedChange={() => toggleSelected(payable.id)}
                        data-testid={`ap-select-${payable.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{payable.vendor_name}</TableCell>
                    <TableCell>{payable.invoice_number || "-"}</TableCell>
                    <TableCell>{getCategoryBadge(payable.category)}</TableCell>
                    <TableCell className="font-semibold">
                      ${payable.amount?.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {payable.due_date
                        ? new Date(payable.due_date).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell>{getDueBadge(payable.days_until_due || 0)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditPayable(payable)}
                          data-testid={`edit-bill-${payable.id}`}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markAsPaid(payable)}
                          className="text-green-600"
                          data-testid={`pay-bill-${payable.id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Mark Paid
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Bill Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={(open) => {
        setAddDialogOpen(open);
        if (!open) {
          setEditingPayable(null);
          setNewPayable({ vendor_name: "", invoice_number: "", amount: "", due_date: "", category: "supplies", notes: "" });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPayable ? "Edit Bill" : "Add New Bill"}</DialogTitle>
            <DialogDescription>{editingPayable ? "Update vendor bill details" : "Record a vendor bill or expense"}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddPayable} className="space-y-4">
            <div className="space-y-2">
              <Label>Vendor Name *</Label>
              <Input
                value={newPayable.vendor_name}
                onChange={(e) => setNewPayable({ ...newPayable, vendor_name: e.target.value })}
                placeholder="e.g., ABC Supply Co."
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Invoice Number</Label>
                <Input
                  value={newPayable.invoice_number}
                  onChange={(e) => setNewPayable({ ...newPayable, invoice_number: e.target.value })}
                  placeholder="e.g., INV-001"
                />
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newPayable.amount}
                  onChange={(e) => setNewPayable({ ...newPayable, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={newPayable.due_date}
                  onChange={(e) => setNewPayable({ ...newPayable, due_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={newPayable.category}
                  onValueChange={(v) => setNewPayable({ ...newPayable, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supplies">Supplies</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="fleet">Fleet</SelectItem>
                    <SelectItem value="utilities">Utilities</SelectItem>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="insurance">Insurance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setAddDialogOpen(false); setEditingPayable(null); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingPayable ? "Update Bill" : "Add Bill"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsPayable;
